import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { queryKeys } from '@/constant/common'
import { IOConfig } from '@/types/controller'
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { resolveSourceInterfaceIds } from './resolveSourceInterfaceIds'

/**
 * Every IO configuration a decoder on this interface can take.
 *
 * A control hub's configs live on its sub-controllers, so this fans out the
 * same way the protocol map does: resolve the sub-controllers, fetch each one's
 * configs, and aggregate. Every config keeps its own `interfaceId` because the
 * config ids repeat across sub-controllers — callers scope a decoder's options
 * to the decoder's own interface.
 *
 * Fetching the whole set once and filtering client-side keeps a large decoder
 * list to one request per sub-controller rather than one per decoder.
 */
function useSearchAllIOConfig(
  interfaceId: number,
): UseQueryResult<IOConfig[], any> {
  const queryClient = useQueryClient()
  return useQuery(
    [queryKeys.controller, 'ioConfigAll', interfaceId],
    async () => {
      const sourceInterfaceIds = await resolveSourceInterfaceIds(
        interfaceId,
        queryClient,
      )

      const results = await Promise.all(
        sourceInterfaceIds.map((id) =>
          controllerDataConfigApi.getIOConfigList(id),
        ),
      )

      const list = results.flatMap((res: any) => {
        const payload = res?.data
        if (Array.isArray(payload?.data)) return payload.data
        if (Array.isArray(payload)) return payload
        return []
      })

      return list.map((item: any) => ({
        id: item.id,
        description: item.description,
        flags: item.flags,
        luTypeProtocolMask: item.luTypeProtocolMask,
        ioTypeMask: item.ioTypeMask,
        config: item.config,
        expression: item.expression,
        interfaceId: item.interfaceId,
      })) as IOConfig[]
    },
    {
      enabled: interfaceId > 0,
      staleTime: 5 * 60 * 1000,
    },
  )
}

export default useSearchAllIOConfig
